import { useGreeting } from '../../ui/greeting';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiError,
  api,
  fmtAgeMinutes,
  type MeAssignment,
  type OnboardingMe,
  type ShiftOverview,
  type ShiftTaskKind,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { EmptyState, QueryBlock } from '../../ui/states';
import { useApi } from './common';
import { useReadOnly } from '../../ui/ReadOnly';
import { CareSetup, FluidsList, HallBoard } from './careShift';
import { CareTodo, type NurseRow } from './careTodo';
import { clock24, newId, type Task } from './careUtil';
import './careShiftV4.css';

// The Console V4 Care Staff overview (handoff "Care Staff overview rebuild", design: the
// roleIsCare block of "Aithora ConsoleV4.dc.html", T6107-6433, logic L44637-45049).
// My shift: a one-line header, a thin shift bar, and one Next up list (urgent first, then
// stops by due time) with a one-tap sheet per row and "Can't get to it". Hall board keeps
// the roster with Fluids under it. To do (was Huddle) is the end of the shift.
// Reads GET /overview/shift and GET /me/assignment; writes through ./care-shift-routes.

export type Kind = ShiftTaskKind;
type Urgent = ShiftOverview['urgent'][number];

export const KIND: Record<Kind, string> = { med: 'MED', task: 'CARE', vitals: 'VITALS', turn: 'TURN/FLUID' };
const VERB: Record<Kind, string> = { med: 'Document', task: 'Complete', vitals: 'Record', turn: 'Done' };
const FILTERS: { key: 'all' | 'overdue' | Kind; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Late' },
  { key: 'med', label: 'Meds' },
  { key: 'task', label: 'Care' },
  { key: 'vitals', label: 'Vitals' },
  { key: 'turn', label: 'Turns & fluids' },
];
const U_KIND: Record<Urgent['kind'], string> = { emergency: 'EMERGENCY', alert: 'ALERT', escalation: 'ESCALATION', detector: 'DETECTOR' };
const U_VERB: Record<Urgent['kind'], string> = { emergency: 'Respond', alert: 'Respond', escalation: 'Acknowledge', detector: 'Go see' };
/** What the alert check calls an open alert when the route sends no label of its own. */
const U_LABEL: Record<Urgent['kind'], string> = { emergency: 'Resident emergency', alert: 'Alert', escalation: 'Crisis escalation', detector: 'Detector flag' };
/** Said on the record when the caregiver continues past the alert check (the server keeps it with who and when). */
const CONTINUE_REASON = 'Continued anyway from the alert check';


/** The design's rel(): "late 3h 48m", "due now", "in 42m". */
function relative(due: number, now: number): string {
  const d = Math.round(Math.abs(due - now) / 60000);
  const s = `${d >= 60 ? `${Math.floor(d / 60)}h ` : ''}${d % 60}m`;
  if (due < now) return `late ${s}`;
  return due - now <= 5 * 60000 ? 'due now' : `in ${s}`;
}

/** Days, Evenings or Nights from the shift's start hour (the route says it when it can). */
function shiftNameOf(startIso: string): 'Days' | 'Evenings' | 'Nights' {
  const h = new Date(startIso).getHours();
  return h >= 5 && h < 13 ? 'Days' : h >= 13 && h < 21 ? 'Evenings' : 'Nights';
}
export const nextShiftOf = (l: 'Days' | 'Evenings' | 'Nights') => (l === 'Days' ? 'Evenings' : l === 'Evenings' ? 'Nights' : 'Days');

/** The "why it matters" line under a stop (design whyOf): dose window, post-fall step, turns, fluids. */
export function whyOf(w: Task, now: number): string {
  const m = /Neuro check (\d) of 4/i.exec(w.what);
  if (m) return `Post-fall sequence, step ${m[1]} of 4. A missed step restarts it.`;
  const due = Date.parse(w.due);
  if (w.kind === 'med') return due < now ? `Dose window closed at ${clock24(due + 3600_000)}. A missed dose goes to the nurse.` : `Give by ${clock24(due + 3600_000)}. After that it counts as missed.`;
  if (/Reposition/.test(w.what)) return 'Every 2 hours. A missed turn raises pressure-injury risk.';
  if (/behind target/.test(w.what)) return 'Behind today’s fluid target.';
  return '';
}

/** The device-assistant phrase on each sheet (design voiceOf): the same write as a tap, via VOICE. */
export function voiceOf(w: Task): string {
  const fn = w.name.split(' ')[0];
  const say = w.kind === 'med' ? `log ${fn}’s dose as given` : w.kind === 'vitals' ? `log vitals for ${fn}` : /fluid/i.test(w.what) ? `log 200 ml for ${fn}` : `mark ${fn}’s ${w.what.split(' · ')[0].toLowerCase()} done`;
  return `Or say “Hey Siri, ${say}” on your phone.`;
}

/** Vitals fields (design FIELDS) and what counts as out of range (outOfRange). */
const FIELDS = [
  ['bp', 'BP', '128/76'],
  ['hr', 'HR', '72'],
  ['temp', 'Temp °F', '98.4'],
  ['spo2', 'SpO₂ %', '96'],
] as const;
type Vitals = Partial<Record<(typeof FIELDS)[number][0], string>>;
function outOfRange(d: Vitals): string[] {
  const f: string[] = [];
  const sys = parseInt((d.bp || '').split('/')[0], 10);
  const hr = parseFloat(d.hr || '');
  const t = parseFloat(d.temp || '');
  const o2 = parseFloat(d.spo2 || '');
  if (sys && (sys > 160 || sys < 90)) f.push('BP');
  if (hr && (hr > 100 || hr < 50)) f.push('HR');
  if (t && t >= 100.4) f.push('temp');
  if (o2 && o2 < 92) f.push('SpO₂');
  return f;
}
const FIELD_FLAG: Record<(typeof FIELDS)[number][0], string> = { bp: 'BP', hr: 'HR', temp: 'temp', spo2: 'SpO₂' };
/** What a stop can end as (design CHOICES): likely answer first. */
const CHOICES: Record<Exclude<Kind, 'vitals'>, [string, string][]> = {
  med: [['given', 'Given'], ['refused', 'Refused'], ['held', 'Held']],
  task: [['done', 'Done'], ['partial', 'Partial'], ['refused', 'Refused']],
  turn: [['done', 'Done'], ['refused', 'Refused']],
};
/** A med stop on an order set to Assistance (the server's `assist`): the Assisted outcomes, never Held. */
const ASSIST_CHOICES: [string, string][] = [['assisted', 'Assisted · taken'], ['assisted_refused', 'Refused'], ['assisted_missed', 'Missed']];


/** "Saved · keeps working offline" while online; the phone says when it is holding writes. */
function useOnline(): boolean {
  const [on, setOn] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    const up = () => setOn(true);
    const down = () => setOn(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return on;
}

type Handed = { offerId: string; to: string; at: string; accepted: boolean };

export function CareStaffOverview() {
  const { client } = useSession();
  const hello = useGreeting();
  const readOnly = useReadOnly();
  const online = useOnline();
  const assignment = useApi<MeAssignment>((c, signal) => api.meAssignment(c, { signal }));
  const setup = useApi<OnboardingMe>((c, signal) => api.onboardingMe(c, { signal }));
  const shift = useApi<ShiftOverview>((c, signal) => api.overviewShift(c, { signal }));
  const [section, setSection] = useState<'shift' | 'todo'>('shift');
  const [tab, setTab] = useState<'work' | 'hall'>('work');
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('all');
  const [sel, setSel] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [passFor, setPassFor] = useState<string | null>(null);
  const [gateOk, setGateOk] = useState<Record<string, true>>({});
  const [drafts, setDrafts] = useState<Record<string, Vitals & { manual?: boolean }>>({});
  const [closed, setClosed] = useState<Record<string, true>>({});
  const [handedLocal, setHandedLocal] = useState<Record<string, Handed>>({});
  const [nurseLocal, setNurseLocal] = useState<NurseRow[]>([]);
  const [fluid, setFluid] = useState<Record<string, number>>({});
  const [taken, setTaken] = useState<Record<string, true>>({});
  const now = new Date();
  const nowMs = now.getTime();
  const a = assignment.data?.assignment ?? null;
  const s = shift.data;

  // What I offered: the route's list (it knows when a coworker accepts), with my taps this
  // session on top until the route has them.
  const handed: Record<string, Handed> = { ...handedLocal };
  for (const p of s?.passed ?? []) handed[p.taskId] = { offerId: p.offerId, to: p.to, at: clock24(Date.parse(p.offeredAt)), accepted: !!p.acceptedAt };
  const waiting = Object.values(handed).some((h) => !h.accepted);
  const { reload } = shift;
  // An offer waits on the coworker: read the shift again until they take it.
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => reload(), 2000);
    return () => clearInterval(t);
  }, [waiting, reload]);

  const urgent = s?.urgent ?? [];
  // View as Care Staff: the server shows a caregiver on shift in the building and names them.
  const shiftOf = assignment.data?.shiftOf ?? s?.shiftOf ?? null;
  const open = s ? s.tasks.filter((w) => !closed[w.id] && !handed[w.id]?.accepted) : [];
  const late = open.filter((w) => Date.parse(w.due) < nowMs);
  const doneN = s ? s.done.length + Object.keys(closed).length : 0;
  // A stop a coworker accepted is still one of this shift's stops (design totAll counts it).
  const total = doneN + open.length + Object.values(handed).filter((h) => h.accepted).length;
  const visible = open.filter((w) => (filter === 'all' || (filter === 'overdue' ? Date.parse(w.due) < nowMs : w.kind === filter)) && (!sel || w.room === sel));
  const pct = (x: number, y: number) => (y ? Math.min(100, Math.round((x / y) * 100)) : 0);
  const start = a ? Date.parse(a.shiftStart) : null;
  const end = a ? Date.parse(a.shiftEnd) : null;
  const nowX = start !== null && end !== null ? pct(Math.max(0, nowMs - start), end - start) : null;
  const shiftName = s?.shiftName ?? (a ? shiftNameOf(a.shiftStart) : null);
  const next = s?.nextShift ?? (shiftName ? nextShiftOf(shiftName) : 'the next shift');
  const hud = s?.huddleList ?? null;
  const [huddleDone, setHuddleDone] = useState<Record<string, boolean>>({});
  const huddleLeft = hud ? hud.rows.filter((r) => !(huddleDone[r.residentId] ?? r.doneAt !== null)).length : s ? s.huddle : 0;
  const selName = sel ? (s?.hall?.find((r) => r.room === sel)?.name ?? a?.residents.find((r) => r.room === sel)?.name ?? '') : '';
  const urgentFor = (w: Task) => urgent.find((u) => (u.residentId && w.residentId ? u.residentId === w.residentId : u.name === w.name)) ?? null;

  const close = async (w: Task, outcome: string, vitals?: Vitals, flagToNurse?: boolean) => {
    const u = urgentFor(w);
    await api.shiftOutcomeV4(client, {
      params: { taskId: w.id },
      body: { outcome, vitals, flagToNurse, clientId: newId(), ...(u && gateOk[w.id] ? { overrideReason: CONTINUE_REASON } : {}) },
    });
    setClosed((c) => ({ ...c, [w.id]: true }));
    setOpenKey(null);
    // What reaches the nurse from this tap (design nurse list): a refusal, or flagged vitals.
    const who = `${w.name}${w.room ? ` · Rm ${w.room}` : ''}`;
    if (outcome === 'refused') {
      const what = w.what.replace(' · document refusal', '');
      setNurseLocal((n) => [...n, { who, what: /^Refused/i.test(what) ? what : `${w.kind === 'med' ? 'Refused ' : 'Refused: '}${what.charAt(0).toLowerCase()}${what.slice(1)}`, red: false }]);
    } else if (vitals && flagToNurse) {
      const f = outOfRange(vitals);
      const vals = [vitals.bp && `BP ${vitals.bp}`, vitals.hr && `HR ${vitals.hr}`, vitals.temp && `${vitals.temp}°F`, vitals.spo2 && `SpO₂ ${vitals.spo2}`].filter(Boolean).join(' · ');
      setNurseLocal((n) => [...n, { who, what: `${f.length ? `Out of range: ${f.join(', ')}` : 'Flagged by you'} · ${vals}`, red: false }]);
    }
  };
  const tryClose = async (w: Task, outcome: string, vitals?: Vitals, flagToNurse?: boolean) => {
    try {
      await close(w, outcome, vitals, flagToNurse);
    } catch (e) {
      // The server's alert check: an emergency nobody has acknowledged on this resident.
      if (e instanceof ApiError && e.status === 409 && (e.body as { gate?: unknown } | undefined)?.gate) {
        setGateOk((g) => {
          const n = { ...g };
          delete n[w.id];
          return n;
        });
        return;
      }
      throw e;
    }
  };
  const offer = async (w: Task, to: { userId: string; name: string }) => {
    const id = newId();
    const r = await api.taskOffer(client, { params: { id: w.id }, body: { to: to.userId, clientId: id } });
    setHandedLocal((h) => ({ ...h, [w.id]: { offerId: r.offerId, to: r.to.name, at: clock24(Date.parse(r.offeredAt)), accepted: false } }));
    setPassFor(null);
    setOpenKey(null);
  };
  const unoffer = async (w: Task) => {
    const h = handed[w.id];
    if (!h) return;
    await api.taskOfferCancel(client, { params: { id: w.id }, body: { offerId: h.offerId } });
    setHandedLocal((x) => {
      const n = { ...x };
      delete n[w.id];
      return n;
    });
    reload();
  };

  const urgentN = urgent.length;
  // Next up counts stops, the same number as "All" and "N stops left"; the urgent rows above
  // them are named on their own, as the header names them.
  const workLabel = urgentN ? `Next up ${open.length} · ${urgentN} urgent` : `Next up ${open.length}`;
  const crew = s?.crew ?? [];

  return (
    <div className="cs4 ov--care" data-testid="overview-care">
      <div className="cs4-sec" role="tablist" aria-label="Overview sections">
        <button role="tab" type="button" aria-selected={section === 'shift'} className={`cs4-sec__tab${section === 'shift' ? ' cs4-sec__tab--on' : ''}`} onClick={() => setSection('shift')}>
          My shift
        </button>
        <button role="tab" type="button" aria-selected={section === 'todo'} className={`cs4-sec__tab${section === 'todo' ? ' cs4-sec__tab--on' : ''}`} onClick={() => setSection('todo')}>
          To do
          {huddleLeft > 0 && <span className="cs4-sec__count">{huddleLeft}</span>}
        </button>
      </div>

      {section === 'todo' ? (
        s ? (
          <CareTodo
            s={s}
            open={open}
            late={late.length}
            now={nowMs}
            shiftEnd={end}
            next={next}
            handed={handed}
            nurseLocal={nurseLocal}
            huddleDone={huddleDone}
            onHuddleDone={async (id, v) => {
              await api.huddleDone(client, { params: { residentId: id }, body: { done: v } });
              setHuddleDone((d) => ({ ...d, [id]: v }));
            }}
          />
        ) : (
          <QueryBlock q={shift} rows={4} isEmpty={() => true} empty={<EmptyState title="No shift assigned right now" body={assignment.data?.reason ?? undefined} />}>
            {() => null}
          </QueryBlock>
        )
      ) : (
        <>
          <CareSetup q={setup} />
          <div className="cs4-head">
            <span className="cs4-head__left">
              <span className="cs4-head__greet">{hello}</span>
              {a
                ? ` · ${shiftOf ? `${shiftOf}’s shift · ` : ''}${a.unit ?? 'No unit set'} · ${a.rooms.length} rooms · ${shiftName} ${clock24(start!)}–${clock24(end!)} · `
                : ` · ${assignment.data ? (assignment.data.reason ?? 'No shift assigned right now.') : 'Loading your shift'} · `}
              <span className="cs4-head__clock">{clock24(nowMs)}</span>
            </span>
            <span className="cs4-head__right">
              <span>
                <span className={`cs4-head__n${urgentN ? ' cs4-head__n--act' : ''}`}>{s ? urgentN : '–'}</span> urgent
              </span>
              <span>
                <span className={`cs4-head__n${late.length ? ' cs4-head__n--late' : ''}`}>{s ? late.length : '–'}</span> late
              </span>
              <span>
                <span className="cs4-head__n">{s ? open.length : '–'}</span> stops left
              </span>
              <span>
                <span className="cs4-head__n">{a ? a.residents.length : '–'}</span> residents
              </span>
              <span className="cs4-head__sync">{online ? 'Saved · keeps working offline' : 'Offline · saved on this device, syncs when back'}</span>
            </span>
          </div>
          <div className="cs4-bar">
            <span className="cs4-bar__end">{start !== null ? clock24(start) : '–'}</span>
            <span className="cs4-bar__track" aria-label={`${doneN} of ${total} stops done, ${late.length} late`}>
              <span className="cs4-bar__done" style={{ width: `${pct(doneN, total)}%` }} />
              <button
                type="button"
                className="cs4-bar__late"
                title="Show late stops"
                aria-label="Show late stops"
                style={{ left: `${pct(doneN, total)}%`, width: `${pct(late.length, total)}%` }}
                onClick={() => {
                  setFilter('overdue');
                  setTab('work');
                }}
              />
              {nowX !== null && <span className="cs4-bar__now" style={{ left: `${nowX}%` }} />}
            </span>
            <span className="cs4-bar__end">{end !== null ? clock24(end) : '–'}</span>
            <span className="cs4-bar__line">{`${doneN} of ${total} done · ${late.length} late`}</span>
          </div>
          <div className="cs4-tabs">
            <span id="cs-tabs" className="cs4-tabs__group" role="tablist" aria-label="Shift work">
              {(
                [
                  ['work', s ? workLabel : 'Next up', null],
                  ['hall', 'Hall board', s?.hall ? s.hall.length : a ? a.residents.length : null],
                ] as const
              ).map(([k, label, n]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} className={`cs4-tab${tab === k ? ' cs4-tab--on' : ''}`} onClick={() => setTab(k)}>
                  {n === null ? label : `${label} ${n}`}
                </button>
              ))}
            </span>
            {tab === 'work' && (
              <span className="cs4-tabs__group">
                {FILTERS.map((f) => {
                  const n = f.key === 'all' ? open.length : f.key === 'overdue' ? late.length : open.filter((w) => w.kind === f.key).length;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      aria-pressed={filter === f.key}
                      className={`cs4-chip${filter === f.key ? ' cs4-chip--on' : f.key === 'overdue' && n ? ' cs4-chip--warn' : ''}`}
                      onClick={() => setFilter(f.key)}
                    >
                      {`${f.label} ${n}`}
                    </button>
                  );
                })}
              </span>
            )}
          </div>

          <div className="cs4-body">
            {!s ? (
              <QueryBlock q={shift} rows={6} isEmpty={() => true} empty={<EmptyState compact title="No shift assigned right now" body={assignment.data?.reason ?? undefined} />}>
                {() => null}
              </QueryBlock>
            ) : tab === 'hall' ? (
              s.hall ? (
                <div className="csx-min">
                  <HallBoard
                    s={{ ...s, tasks: open }}
                    now={nowMs}
                    sel={sel}
                    onSel={(room) => {
                      setSel(room);
                      setFilter('all');
                      setTab('work');
                    }}
                  />
                  {s.fluids && (
                    <div className="cs4-fluids">
                      <FluidsList
                        s={s}
                        added={fluid}
                        onAdd={async (id) => {
                          await api.shiftFluid(client, { body: { residentId: id, ml: 120 } });
                          setFluid((f) => ({ ...f, [id]: (f[id] ?? 0) + 120 }));
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState compact title="The hall board is not served yet" waitingOn="hall on GET /overview/shift" />
              )
            ) : (
              <>
                {sel && (
                  <span className="cs4-sel">
                    <span className="cs4-sel__label">{`Showing Rm ${sel} · ${selName} only`}</span>
                    <button type="button" className="cs4-sel__clear" onClick={() => setSel(null)}>
                      Show all
                    </button>
                  </span>
                )}
                <div className="cs4-grid">
                  {urgent.map((u) => {
                    const red = u.kind !== 'detector';
                    return (
                      <div key={u.id} className="cs4-u" data-testid={`cs4-urgent-${u.residentId ?? u.id}`}>
                        <span className={`cs4-u__age${red ? ' cs4-u__age--red' : ''}`}>{fmtAgeMinutes((nowMs - Date.parse(u.since)) / 60000)}</span>
                        <span className={`cs4-u__kind${red ? '' : ' cs4-u__kind--blue'}`}>{U_KIND[u.kind]}</span>
                        <span className="cs4-main">
                          <Link to="/residents" className="cs4-name">
                            {u.name} <span className="cs4-room">{u.room ? `Rm ${u.room}` : ''}</span>
                          </Link>
                          <span className="cs4-what">{u.what}</span>
                        </span>
                        {u.owner && !u.owner.you ? (
                          // Someone else has taken it on: say who, rather than send a second person.
                          <span className="cs4-u__owner">{`${u.owner.name} is on it`}</span>
                        ) : (
                          <Link to="/guardian" className={`cs4-u__btn${red ? ' cs4-u__btn--red' : ''}`}>
                            {U_VERB[u.kind]}
                          </Link>
                        )}
                      </div>
                    );
                  })}
                  {(s.offeredToMe ?? []).filter((o) => !o.acceptedAt && !taken[o.offerId]).map((o) => (
                    // A stop a coworker offered me (no design state: drawn as an urgent row).
                    <div key={o.offerId} className="cs4-u">
                      <span className="cs4-u__age">{clock24(Date.parse(o.offeredAt))}</span>
                      <span className="cs4-u__kind cs4-u__kind--blue">OFFERED</span>
                      <span className="cs4-main">
                        <span className="cs4-name">{`From ${o.from}`}</span>
                        <span className="cs4-what">Can you take this stop? It moves to your list when you do.</span>
                      </span>
                      <button
                        type="button"
                        className="cs4-u__btn"
                        disabled={readOnly}
                        onClick={async () => {
                          await api.taskAccept(client, { params: { id: o.taskId }, body: { offerId: o.offerId } });
                          setTaken((t) => ({ ...t, [o.offerId]: true }));
                          reload();
                        }}
                      >
                        Take it
                      </button>
                    </div>
                  ))}
                  {visible.map((w) => {
                    const due = Date.parse(w.due);
                    const isLate = due < nowMs;
                    const tone = isLate ? 'late' : due - nowMs <= 5 * 60000 ? 'soon' : 'ahead';
                    const isOpen = openKey === w.id;
                    const d = drafts[w.id] ?? {};
                    const flags = outOfRange(d);
                    const filled = FIELDS.some(([k]) => (d[k] || '').trim());
                    const why = w.passedFrom ? `Passed to you by ${w.passedFrom}.` : whyOf(w, nowMs);
                    const u = urgentFor(w);
                    const gate = isOpen && !!u && !gateOk[w.id];
                    const h = handed[w.id];
                    return (
                      <div key={w.id} className={`cs4-w${isOpen ? ' cs4-w--open' : ''}`}>
                        <div className="cs4-w__row">
                          <span className={`cs4-w__at cs4--${tone}`}>{clock24(due)}</span>
                          <span className="cs4-w__kind">{KIND[w.kind]}</span>
                          <span className="cs4-main">
                            <Link to="/residents" className="cs4-name">
                              {w.name} <span className="cs4-room">{w.room ? `Rm ${w.room}` : ''}</span> <span className={`cs4-when cs4--${tone}`}>{relative(due, nowMs)}</span>
                            </Link>
                            <span className="cs4-what">{w.what}</span>
                            {why && <span className={`cs4-why${isLate ? ' cs4--late' : ''}`}>{why}</span>}
                          </span>
                          <button
                            type="button"
                            className="cs4-w__pass"
                            title="Can't get to it: pass to a coworker"
                            disabled={readOnly || !!h}
                            onClick={() => {
                              setPassFor(passFor === w.id ? null : w.id);
                              setOpenKey(null);
                            }}
                          >
                            {h ? '' : passFor === w.id ? 'Cancel' : 'Can’t get to it'}
                          </button>
                          <button type="button" className={`cs4-w__btn${isOpen ? ' cs4-w__btn--open' : ''}`} disabled={readOnly} onClick={() => setOpenKey(isOpen ? null : w.id)}>
                            {isOpen ? 'Cancel' : VERB[w.kind]}
                          </button>
                        </div>
                        {passFor === w.id && !h && (
                          <div className="cs4-pass">
                            <span className="cs4-pass__k">Pass to</span>
                            {crew.length === 0 && <span className="cs4-pass__k">Nobody else is on shift in your building right now.</span>}
                            {crew.map((c) => (
                              <button key={c.userId} type="button" className="cs4-pass__who" onClick={() => offer(w, c)}>
                                {`${c.name} · ${c.role}${c.residents === null ? '' : ` · ${c.residents} residents`}`}
                              </button>
                            ))}
                          </div>
                        )}
                        {isOpen && (
                          <div className="cs4-sheet">
                            {gate ? (
                              <span className="cs4-gate">
                                <span className="cs4-gate__text">{`${u.label ?? U_LABEL[u.kind]} still open for ${w.name}. Respond first?`}</span>
                                <Link to="/guardian" className="cs4-btn44 cs4-btn44--red">
                                  Respond first
                                </Link>
                                <button type="button" className="cs4-btn44 cs4-btn44--quiet" onClick={() => setGateOk((g) => ({ ...g, [w.id]: true }))}>
                                  Continue anyway
                                </button>
                              </span>
                            ) : (
                              <>
                                {w.kind === 'vitals' ? (
                                  <>
                                    <span className="cs4-fields">
                                      {FIELDS.map(([k, label, ph]) => (
                                        <label key={k} className="cs4-field">
                                          <span className="cs4-field__k">{label}</span>
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            className={`cs4-field__in${flags.includes(FIELD_FLAG[k]) ? ' cs4-field__in--bad' : ''}`}
                                            placeholder={ph}
                                            value={d[k] ?? ''}
                                            onChange={(e) => setDrafts((x) => ({ ...x, [w.id]: { ...(x[w.id] ?? {}), [k]: e.target.value } }))}
                                          />
                                        </label>
                                      ))}
                                    </span>
                                    <span className="cs4-vrow">
                                      <span className={`cs4-range${flags.length ? ' cs4-range--bad' : ''}`}>
                                        {flags.length
                                          ? `Out of range: ${flags.join(', ')}. The nurse is paged when you save.`
                                          : filled
                                            ? 'In range. Save writes to the chart and the detectors.'
                                            : 'Enter what you took. Anything out of range pages the nurse automatically.'}
                                      </span>
                                      <button
                                        type="button"
                                        className="cs4-btn44 cs4-btn44--line"
                                        disabled={!w.residentId}
                                        onClick={async () => {
                                          const r = await api.vitalsLast(client, { params: { id: w.residentId! } });
                                          setDrafts((x) => ({
                                            ...x,
                                            [w.id]: { ...(x[w.id] ?? {}), ...(r.bp ? { bp: r.bp.value } : {}), ...(r.hr ? { hr: r.hr.value } : {}), ...(r.temp ? { temp: r.temp.value } : {}), ...(r.spo2 ? { spo2: r.spo2.value } : {}) },
                                          }));
                                        }}
                                      >
                                        Use last reading
                                      </button>
                                      <button type="button" className={`cs4-flag${d.manual ? ' cs4-flag--on' : ''}`} onClick={() => setDrafts((x) => ({ ...x, [w.id]: { ...(x[w.id] ?? {}), manual: !d.manual } }))}>
                                        {d.manual ? 'Flagged to nurse' : 'Flag to nurse'}
                                      </button>
                                      <button
                                        type="button"
                                        className={`cs4-save${filled ? ' cs4-save--on' : ''}`}
                                        disabled={!filled}
                                        onClick={() => tryClose(w, 'recorded', { bp: d.bp, hr: d.hr, temp: d.temp, spo2: d.spo2 }, !!d.manual || flags.length > 0)}
                                      >
                                        Save
                                      </button>
                                    </span>
                                  </>
                                ) : (
                                  <span className="cs4-choices">
                                    {(w.kind === 'med' && w.assist ? ASSIST_CHOICES : CHOICES[w.kind]).map(([k, label]) => (
                                      <button key={k} type="button" className={`cs4-choice${k === 'given' || k === 'done' || k === 'assisted' ? ' cs4-choice--go' : k === 'refused' || k === 'assisted_refused' ? ' cs4-choice--refused' : ''}`} onClick={() => tryClose(w, k)}>
                                        {label}
                                      </button>
                                    ))}
                                  </span>
                                )}
                                <span className="cs4-voice">{voiceOf(w)}</span>
                              </>
                            )}
                          </div>
                        )}
                        {h && (
                          <span className="cs4-outcome">
                            {`Offered to ${h.to} at ${h.at} · waiting for them to accept`}
                            <button type="button" className="cs4-link" onClick={() => unoffer(w)}>
                              Undo
                            </button>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {visible.length === 0 && <span className="cs4-empty">Nothing left in this view.</span>}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
